// Sub-components extracted from SrDetailPage.

import { useState, type JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ServiceType, ServiceCategory, ConsultBlock } from "@hcm/core";
import { SrStatusBadge } from "../components/SrStatusBadge.js";
import { RiskBadge } from "../components/RiskBadge.js";
import { apiFetch, getAccessToken } from "../lib/api.js";

const IST_TZ = "Asia/Kolkata";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";

export interface SrDetail {
  _id: string; status: string;
  service_type: ServiceType;
  // Q7 D2 — derived server-side via Mongoose setter; consumed for
  // assignment-UI branching instead of an SR-side hardcoded set.
  category: ServiceCategory;
  patient_id: string; assigned_carer_id: string | null;
  scheduled_datetime?: string; special_instructions?: string; prescription_filename?: string | null;
  nsofar_sr_id?: string | null;
  assigned_agent_id: string | null; assigned_agent_type: string | null;
  payment_status: string; report_compliant: boolean | null;
  risk_score_band?: string | null; created_at: string;
  csat_score: number | null;
  actual_start_datetime?: string | null;
  actual_end_datetime?: string | null;
  dispute_flag?: boolean;
  potential_no_show_flag?: boolean;
  family_satisfaction_rating?: number | null;
  family_feedback_notes?: string | null;
  carer_requested_again?: boolean;
  carer_excluded_by_family?: boolean;
  incident_during_assignment?: boolean;
  // Flat human-readable contact + request fields synced from Nsofar.
  // The order of keys is preserved by the backend and rendered as-is
  // by RequestDetailsCard. Optional — older SRs and non-Nsofar SRs
  // won't have it.
  nsofar_sr_fields?: Record<string, string> | null;
  // Structured parent profile address (TASK-012). Permissive shape —
  // the SPEC §5 keys are: full_address, flat_house_no, building,
  // street_area, landmark, city, state, pincode, lat?, lng?.
  service_address?: Record<string, unknown> | null;
  // nsofar doctor-consult sync (docs/33). source distinguishes nsofar-owned
  // consult SRs (lifecycle read-only in HCM; driven via ConsultPanel actions)
  // from anything created locally.
  source?: string;
  consult?: ConsultBlock | null;
}

// nsofar-owned consult SR (docs/33 §7.7): the coarse status is written by the
// sync receiver only — manual transitions 409 server-side, so the transition
// UI is hidden and the ConsultPanel is the sole driver.
export function isNsofarConsultSr(sr: Pick<SrDetail, "service_type" | "source">): boolean {
  return sr.service_type === "tele_consult" && sr.source === "nsofar_sync";
}

// Status transitions allowed from each state (from validateTransition in @hcm/core)
const NEXT_STATUSES: Record<string, string[]> = {
  new:         ["assigned", "cancelled"],
  assigned:    ["scheduled", "cancelled"],
  scheduled:   ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed:   ["confirmed", "disputed"],
  confirmed:   [],
  disputed:    [],
  cancelled:   [],
};

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="w-36 text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-800">{value}</span>
    </div>
  );
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
export function SrInfoCard({ sr }: { sr: SrDetail }): JSX.Element {
  const dateStr = sr.scheduled_datetime
    ? new Date(sr.scheduled_datetime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: IST_TZ })
    : "—";
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <div className="flex items-center gap-3">
        <SrStatusBadge status={sr.status} />
        <RiskBadge band={sr.risk_score_band ?? null} />
      </div>
      <InfoRow label="Type" value={sr.service_type.replace(/_/g, " ")} />
      <InfoRow label="Patient ref" value={`P-${sr.patient_id.slice(-5).toLowerCase()}`} />
      {sr.category === "care_visit" && (
        <InfoRow label="Carer ref" value={sr.assigned_carer_id ? `C-${sr.assigned_carer_id.slice(-5).toLowerCase()}` : "Unassigned"} />
      )}
      {sr.assigned_agent_id && (
        <InfoRow label="Agent" value={`A-${sr.assigned_agent_id.slice(-5).toLowerCase()} (${sr.assigned_agent_type?.replace(/_/g, " ") ?? "—"})`} />
      )}
      <InfoRow label="Scheduled" value={dateStr} />
      <InfoRow label="Payment" value={sr.payment_status} />
      <InfoRow label="Compliant" value={sr.report_compliant === null ? "—" : sr.report_compliant ? "Yes" : "No"} />
      {sr.actual_start_datetime && (
        <InfoRow label="Actual start" value={new Date(sr.actual_start_datetime).toLocaleString("en-IN", { timeStyle: "short", dateStyle: "medium", timeZone: IST_TZ })} />
      )}
      {sr.actual_end_datetime && (
        <InfoRow label="Actual end" value={new Date(sr.actual_end_datetime).toLocaleString("en-IN", { timeStyle: "short", dateStyle: "medium", timeZone: IST_TZ })} />
      )}
      {sr.dispute_flag && <InfoRow label="Dispute" value="Flagged" />}
      {sr.potential_no_show_flag && <InfoRow label="No-show risk" value="Flagged" />}
      {sr.special_instructions && <InfoRow label="Instructions" value={sr.special_instructions} />}
      {/* Prescriptions render in their own card (PrescriptionsCard) — multi-file, S3-backed (docs/17). */}
    </div>
  );
}

// ── Prescriptions (docs/17) ───────────────────────────────────────────────────

export interface PrescriptionItem {
  s3_key: string | null;   // null = legacy single-file SR (download via /sr/:id/prescription)
  filename: string;
  content_type: string;
  size: number;
  uploaded_at: string;
  source: string;
  legacy: boolean;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Stream a JWT-guarded endpoint (zip bundle / legacy buffer) to a file. The
// server sets Content-Disposition; we just trigger the browser save.
async function streamDownload(path: string, filename: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`download_failed_${res.status}`);
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
}

async function downloadOne(srId: string, file: PrescriptionItem): Promise<void> {
  if (file.s3_key) {
    // S3-backed — request a short-lived, tenant-guarded signed URL and open it.
    const { url } = await apiFetch<{ url: string }>(`/uploads/signed-url?file_key=${encodeURIComponent(file.s3_key)}`);
    window.open(url, "_blank", "noopener");
    return;
  }
  // Legacy single-file SR — stream the inline buffer.
  await streamDownload(`/sr/${srId}/prescription`, file.filename);
}

/**
 * Lists every prescription file on an SR with a per-file download, plus a
 * "Download all" (zip) when there is more than one. Renders nothing when the SR
 * has no prescriptions, so non-prescription SRs show no UI delta. The list
 * includes the legacy read-merge shim entry for old single-file SRs.
 */
export function PrescriptionsCard({ srId }: { srId: string }): JSX.Element | null {
  const { data: files, isLoading, isError } = useQuery<PrescriptionItem[]>({
    queryKey: ["sr", srId, "prescriptions"],
    queryFn: () => apiFetch<PrescriptionItem[]>(`/sr/${srId}/prescriptions`),
  });
  const [busy, setBusy] = useState(false);

  if (isLoading || isError || !Array.isArray(files) || files.length === 0) return null;

  const run = (fn: () => Promise<void>): void => {
    setBusy(true);
    void fn().catch(() => alert("Download failed. Please try again.")).finally(() => setBusy(false));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Prescriptions ({files.length})</h2>
        {files.length > 1 && (
          <button disabled={busy}
            onClick={() => run(() => streamDownload(`/sr/${srId}/prescriptions/download-all`, `SR-${srId}-prescriptions.zip`))}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50">
            ⬇ Download all
          </button>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {files.map((f, i) => (
          <li key={f.s3_key ?? `legacy-${i}`} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="text-sm text-slate-800 truncate">{f.filename}</p>
              <p className="text-xs text-slate-400">{formatBytes(f.size)}{f.legacy ? " · legacy" : ""}</p>
            </div>
            <button disabled={busy} onClick={() => run(() => downloadOne(srId, f))}
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Download
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Renders the Nsofar Request Details block as a dynamic label/value
 * table. Fully data-driven — no service-type-specific code, no
 * hardcoded field names. The backend (`build_nsofar_sr_fields` in
 * Nsofar's hcm_client.py) decides which fields to include and in
 * what order; this card just iterates the dict.
 *
 * Renders nothing when nsofar_sr_fields is absent or empty, so
 * non-Nsofar SRs and SRs created before this feature shipped show
 * no UI delta — fully backwards compatible.
 */
export function RequestDetailsCard({ fields }: { fields: Record<string, string> | null | undefined }): JSX.Element | null {
  if (!fields) return null;
  const entries = Object.entries(fields);
  if (entries.length === 0) return null;
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-2"
      data-testid="request-details-card"
    >
      <h2 className="text-sm font-semibold text-slate-700">Request Details</h2>
      <div className="space-y-1.5">
        {entries.map(([label, value]) => (
          <InfoRow key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  );
}

export interface CheckinProps {
  checkin: { mutate: () => void; isPending: boolean; isError: boolean };
  checkout: { mutate: () => void; isPending: boolean; isError: boolean };
}

export function CheckinSection({ checkin, checkout }: CheckinProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">Check-in / Check-out</h2>
      <div className="flex gap-3">
        <button onClick={() => checkin.mutate()} disabled={checkin.isPending}
          className="text-sm font-medium px-4 py-2 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50">
          {checkin.isPending ? "Checking in…" : "Check In"}
        </button>
        <button onClick={() => checkout.mutate()} disabled={checkout.isPending}
          className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {checkout.isPending ? "Checking out…" : "Check Out"}
        </button>
      </div>
      {(checkin.isError || checkout.isError) && (
        <p className="text-sm text-red-600">Check-in/out failed. Try again.</p>
      )}
    </div>
  );
}

export function TransitionButtons({ sr, onTransition }: { sr: SrDetail; onTransition: (s: string) => void }): JSX.Element {
  const next = NEXT_STATUSES[sr.status] ?? [];
  if (next.length === 0) return <p className="text-sm text-slate-400 italic">No further transitions available.</p>;
  const colorMap: Record<string, string> = {
    cancelled: "border border-red-300 text-red-600 hover:bg-red-50",
    confirmed: "bg-emerald-600 text-white hover:bg-emerald-700",
  };
  return (
    <div className="flex flex-wrap gap-2">
      {next.map((s) => (
        <button key={s} onClick={() => onTransition(s)}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors capitalize ${colorMap[s] ?? "bg-navy-700 text-white hover:bg-navy-800"}`}>
          {s.replace(/_/g, " ")}
        </button>
      ))}
    </div>
  );
}

export const CSAT_STATUSES = new Set(["completed", "confirmed", "disputed"]);
export const FAM_SAT_STATUSES = new Set(["completed", "confirmed"]);

export function CsatSection({ existing, csat }: { existing: number | null; csat: { mutate: (n: number) => void; isPending: boolean; isError: boolean } }): JSX.Element {
  const [score, setScore] = useState<number | null>(null);
  if (existing !== null) return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">CSAT Score</h2>
      <p className="text-2xl font-bold text-navy-700">{existing}<span className="text-sm font-normal text-slate-500"> / 5</span></p>
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">Manual CSAT Entry</h2>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setScore(n)}
            className={`w-9 h-9 rounded-full text-sm font-semibold border transition-colors ${score === n ? "bg-navy-700 text-white border-navy-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {n}
          </button>
        ))}
      </div>
      <button onClick={() => { if (score) csat.mutate(score); }} disabled={!score || csat.isPending}
        className="text-sm font-medium px-4 py-2 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50">
        {csat.isPending ? "Saving…" : "Save Score"}
      </button>
      {csat.isError && <p className="text-sm text-red-600">Failed to save CSAT.</p>}
    </div>
  );
}

export function FamilySatisfactionSection({
  existing,
  notes,
  onSave,
}: {
  existing: number | null;
  notes?: string | null;
  onSave: (n: number) => void;
}): JSX.Element {
  const [rating, setRating] = useState<number | null>(null);
  if (existing !== null) return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-1">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Family Satisfaction Rating</h2>
      <p className="text-2xl font-bold text-navy-700">{existing}<span className="text-sm font-normal text-slate-500"> / 5</span></p>
      {notes && <p className="text-sm text-slate-600 pt-1">{notes}</p>}
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">Family Satisfaction Rating (DRS §E1)</h2>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)}
            className={`w-9 h-9 rounded-full text-sm font-semibold border transition-colors ${rating === n ? "bg-navy-700 text-white border-navy-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {n}
          </button>
        ))}
      </div>
      <button onClick={() => { if (rating) onSave(rating); }} disabled={!rating}
        className="text-sm font-medium px-4 py-2 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50">
        Save Rating
      </button>
    </div>
  );
}
